import React, { memo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { SunfocusLogo } from '../../theme/SvgIcons';
import { updatePasswordApi } from '../../services/api_collection';
import styled from '@emotion/styled/macro';
import signBg from '../../assets/BACKGROUND.svg';
import {
  resetUserInfoSlice,
  setUpdateLoading,
  setUserInfo
} from '../../redux/sign-in/userInfoSlice';
import { Button, Typography, Form, Input } from 'antd';
import { userTheme } from '../../theme/themeComponent';

const { Title, Text } = Typography;
const { Password } = Input;

function UpdatePassword() {
  const [form] = Form.useForm();
  const { id } = useLocation()?.state || {};
  const { updateLoading } = useSelector((e) => e.userInfo);
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const onSubmit = async (values) => {
    const res = await updatePasswordApi(id, { password: values?.newPassword });
    dispatch(setUpdateLoading(true));
    if (res?.statusCode === 200) {
      dispatch(resetUserInfoSlice());
      dispatch(setUserInfo(null));
      toast.success('Password Updated Successfully');
      navigate('/');
    } else {
      dispatch(setUpdateLoading(false));
      toast.error(res?.message || 'Something went wrong');
    }
  };

  return (
    <Wrapper userTheme={userTheme}>
      <div className="left-section">
        <img src={signBg} alt="background" className="bgImage" />
      </div>
      <BoxWrapper>
        <div className="logo">
          <SunfocusLogo />
        </div>
        <CardStyle>
          <div>
            <Title
              level={1}
              style={{
                fontSize: '48px',
                lineHeight: '50px',
                fontWeight: '700',
                margin: 0
              }}>
              Update Password
            </Title>
            <Text style={{ fontSize: '16px', color: '#767676' }}>
              Create a strong and secure password.
            </Text>
          </div>
          <Form form={form} onFinish={onSubmit} layout="vertical">
            <Form.Item
              label={<Text style={{ fontSize: '16px', fontWeight: 400 }}>New Password</Text>}
              name="newPassword"
              required={false}
              rules={[
                { required: true, message: 'Password is required' },
                {
                  pattern:
                    /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[!@#$%^&*[\]{}()?"\\,><':;|_~`=+-])[a-zA-Z\d!@#$%^&*[\]{}()?"\\,><':;|_~`=+-]{8,99}$/,
                  message: 'Must contain at least 8 Characters, Example - Abcd@123'
                }
              ]}
              validateFirst>
              <Password
                placeholder="Enter New Password"
                size="large"
                style={{
                  height: '47px',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\s/g, '');
                  e.target.value = cleaned;
                }}
              />
            </Form.Item>
            <Form.Item
              label={
                <Text style={{ fontSize: '16px', fontWeight: 400 }}>Confirm New Password</Text>
              }
              name="confirmPassword"
              required={false}
              dependencies={['newPassword']}
              rules={[
                { required: true, message: 'Enter Confirm Password' },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue('newPassword') === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(new Error('Password must match'));
                  }
                })
              ]}
              validateFirst>
              <Password
                placeholder="Enter Confirm Password"
                size="large"
                style={{
                  height: '47px',
                  borderRadius: '8px',
                  fontSize: '16px'
                }}
                onKeyDown={(e) => {
                  if (e.key === ' ') {
                    e.preventDefault();
                  }
                }}
                onChange={(e) => {
                  const cleaned = e.target.value.replace(/\s/g, '');
                  e.target.value = cleaned;
                }}
              />
            </Form.Item>
            <Form.Item style={{ margin: 0 }}>
              <Button
                htmlType="submit"
                type="primary"
                loading={updateLoading}
                className="buttonStyle">
                Update Password
              </Button>
            </Form.Item>
          </Form>
        </CardStyle>
      </BoxWrapper>
    </Wrapper>
  );
}

export default memo(UpdatePassword);

const Wrapper = styled.div`
  display: flex;
  width: 100%;
  height: 100vh;

  .left-section {
    display: flex;
    justify-content: center;
    align-items: center;
  }

  .bgImage {
    height: 100vh;
    @media (max-width: 1000px) {
      display: none !important;
    }
  }

  .logo {
    position: absolute;
    top: 20px;
    right: 30px;
  }

  .buttonStyle {
    width: 100%;
    background-color: ${({ userTheme }) => userTheme.primaryColor};
    color: #fff;
    text-transform: capitalize;
    height: 50px;
    margin-top: 20px;
    &:hover {
      background-color: ${({ userTheme }) => userTheme.primaryColor} !important;
      opacity: 0.9;
    }
  }

  .form-field {
    width: 100%;
    margin: 16px 0;
  }
`;

const BoxWrapper = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 50px;
  align-items: center;
  padding: 2rem;
  position: relative;
  justify-content: center;
`;

export const CardStyle = styled.div`
  display: flex;
  flex-direction: column;
  width: 460px;
  padding: 2rem;
  gap: 20px;

  @media (max-width: 450px) {
    width: 400px;
  }

  @media (max-width: 350px) {
    width: 380px;
  }
`;
